import { Box, Button, Typography } from "@mui/material";
import Carousel from "react-multi-carousel";
import scatterData from "../../datas/scatter2dData.json";
import { useRecoilState } from "recoil";
import { selectedAlgoState } from "../../recoil";
import { useEffect, useState } from "react";
import { COLORS } from "../../styles/GlobalStyles";
import { t } from "i18next";
import Plot from "react-plotly.js";
import { getLastPathSegment } from "../../utils/datasetFileUtil";
import { IFileObject } from "@types";
import { transFiles } from "../../utils/utils";

interface CustomPreOptionProps {
  flatFiles: string[];
  setFiles: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
  labelCount: number;
}

export default function CustomPreOption({
  flatFiles,
  setFiles,
  labelCount,
}: CustomPreOptionProps) {
  const algoList = [
    { id: "bd31fe2a-4c6a-4919-bebb-47795f578080", name: t("AlgorithmA") },
    { id: "b8e0312e-2ba7-41bc-b4f2-76fef881dd0e", name: t("AlgorithmB") },
    { id: "e7eb3350-efee-41db-96e8-bf91ea2a28b4", name: t("AlgorithmC") },
    { id: "28c20f9b-1edd-4948-b275-0315d417edb6", name: t("AlgorithmD") },
  ];
  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
      slidesToSlide: 1,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
      slidesToSlide: 1,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1,
      slidesToSlide: 1,
    },
  };

  return (
    <Box
      sx={{
        height: "400px",
        "& .react-multi-carousel-list.container": {
          alignItems: "flex-start !important",
        },
        "& .react-multiple-carousel__arrow": {
          top: "30% !important",
          transform: "translateY(-30%)",
        },
        "& .react-multiple-carousel__arrow--right": {
          right: 0,
        },
        "& .react-multiple-carousel__arrow--left": {
          left: 0,
        },
      }}
    >
      <Typography>{`${labelCount}${t("ClassCountSuffix")}`}</Typography>
      <Box sx={{ height: "fit-content" }}>
        <Carousel
          additionalTransfrom={0}
          arrows
          autoPlaySpeed={3000}
          centerMode={false}
          className=""
          containerClass="container"
          dotListClass=""
          draggable
          focusOnSelect={false}
          keyBoardControl
          minimumTouchDrag={80}
          partialVisible
          pauseOnHover
          renderArrowsWhenDisabled={false}
          renderButtonGroupOutside={false}
          renderDotsOutside={false}
          responsive={responsive}
          rewind={false}
          rewindWithAnimation={false}
          rtl={false}
          shouldResetAutoplay
          showDots={false}
          sliderClass=""
          slidesToSlide={1}
          swipeable
        >
          {algoList.map((algoItem, index) => (
            <Box sx={{ width: "300px" }}>
              <Typography>{algoItem.name}</Typography>
              <PreClassChart
                algoItem={algoItem}
                images={flatFiles}
                classList={
                  labelCount > 0
                    ? Array.from({ length: labelCount }, (_, i) =>
                        String.fromCharCode(65 + i)
                      )
                    : ["OK", "NG"]
                }
                setFiles={setFiles}
              />
            </Box>
          ))}
        </Carousel>
      </Box>
    </Box>
  );
}

interface ScatterData {
  x: number;
  y: number;
}

interface GroupedData {
  [key: string]: ScatterData[];
}

const ScatterPlot = ({
  data,
  groupedData,
  classList,
}: {
  data: Array<any>;
  groupedData: GroupedData;
  classList: string[];
}) => {
  const traces = classList.map((className, index) => {
    const classPoints = data[0].x
      .map((x: number, i: number) => ({
        x: x,
        y: data[0].y[i],
        label: data[0].marker.color[i] === COLORS[index] ? className : null,
        path: data[0].text[i],
      }))
      .filter((point: any) => point.label === className);

    return {
      x: classPoints.map((p: any) => p.x),
      y: classPoints.map((p: any) => p.y),
      imageId: classPoints.map((p: any) => p.id),
      text: classPoints.map((p: any) => getLastPathSegment(p.path)),
      mode: "markers",
      name: className,
      marker: {
        size: 10,
        color: COLORS[index],
      },
      type: "scatter",
      hoverinfo: "test",
    };
  });

  return (
    <Plot
      data={traces as any}
      layout={{
        margin: { l: 0, r: 0, t: 0, b: 0 },
        showlegend: true,
        width: 270,
        xaxis: {
          fixedrange: true,
        },
        yaxis: {
          fixedrange: true,
        },
      }}
      config={{ displayModeBar: false }}
    />
  );
};

interface PreClassChartProps {
  algoItem: any;
  images: string[];
  classList: string[];
  setFiles: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
}

const PreClassChart = ({
  algoItem,
  images,
  classList,
  setFiles,
}: PreClassChartProps) => {
  const COLORS = [
    "red",
    "blue",
    "green",
    "orange",
    "purple",
    "pink",
    "gray",
    "brown",
    "cyan",
  ];

  const [selectAlgorithm, setSelectAlgorithm] =
    useRecoilState(selectedAlgoState);
  const [groupedData, setGroupedData] = useState<GroupedData>({
    unlabeled: scatterData.unlabeled,
    firstG: [],
    secondG: [],
    thirdG: [],
  });
  const [updateImages, setUpdateImages] = useState<any[]>([]);
  const [labels, setLabels] = useState<string[]>([]);

  useEffect(() => {
    setLabels(classList);
    const updatedImages = addRandomCoordinates(images).map((img) => {
      const randomLabel =
        classList[Math.floor(Math.random() * classList.length)];
      return {
        ...img,
        AILabel: randomLabel,
        recentLabel: randomLabel,
      };
    });

    setUpdateImages(updatedImages);
  }, []);

  // 랜덤 숫자를 생성하는 함수
  const getRandomValue = (min: number, max: number): number => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // images 배열에 x와 y 값을 추가하는 함수
  const addRandomCoordinates = (images: string[]): Array<any> => {
    return images.map((image) => ({
      path: image,
      x: getRandomValue(-10, 10),
      y: getRandomValue(-10, 10),
    }));
  };

  const scatterDatas = [
    {
      x: updateImages.map((img) => img.x), // x 좌표 배열
      y: updateImages.map((img) => img.y), // y 좌표 배열
      imageId: updateImages.map((img) => img.id),
      text: updateImages.map((img) => img.path), // 이미지 경로를 hover로 표시
      mode: "markers",
      marker: {
        size: 10,
        color: updateImages.map((img) => {
          const labelIndex = labels.indexOf(img.recentLabel);
          return labelIndex !== -1
            ? COLORS[labelIndex % COLORS.length]
            : "black";
        }),
      },
    },
  ];

  const convertToFileObjects = (images: any[]): IFileObject[] => {
    return images.map((img, index) => ({
      id: index,
      path: img.path,
      label: img.AILabel,
    }));
  };

  const handleSelectAlgorithm = () => {
    console.log(updateImages);
    // setFiles
    const fileObjects = convertToFileObjects(updateImages);
    const formatFiles = transFiles(fileObjects);
    console.log(formatFiles);
    setFiles(transFiles(fileObjects));
    setSelectAlgorithm({
      ...selectAlgorithm,
      algoId: algoItem.id,
      algoName: algoItem.name,
      images: updateImages,
      labels: labels,
    });
  };

  return (
    <Box sx={{ width: "fit-content", height: "500px" }}>
      <ScatterPlot
        data={scatterDatas}
        groupedData={groupedData}
        classList={labels}
      />
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleSelectAlgorithm}>
          {t("Select")}
        </Button>
      </Box>
    </Box>
  );
};
